import { useEffect, useMemo, useRef, useState } from 'react'
import { GridController } from '../compositor/GridController'
import {
  applyFormulaKeyCommand,
  canCommitFormulaSessionOnSheet,
  getFormulaFunctionAssistance,
  insertFormulaFunctionSuggestion,
  isActiveFormulaReferenceEditSession,
  resolveFormulaKeyCommand,
  type FormulaCommitNavigation,
  type FormulaEditSession,
  updateFormulaDraft,
  updateFormulaSelection,
} from '../spreadsheet/formulaAuthoring'
import { getFormulaNameAutocomplete } from '../spreadsheet/formulaNameAutocomplete'
import FormulaFunctionAssistant, { type FormulaNameSuggestion } from './FormulaFunctionAssistant'
import FormulaSyntaxOverlay from './FormulaSyntaxOverlay'

type FormulaBarDefinedName = FormulaNameSuggestion & {
  hidden?: boolean
}

interface FormulaBarProps {
  controller: GridController
  liveValue?: string | null
  formulaSession?: FormulaEditSession | null
  currentSheetName?: string
  expanded?: boolean
  definedNames?: FormulaBarDefinedName[]
  onBeginFormulaEdit?: (value: string, caretStart: number, caretEnd: number) => void
  onDraftChange?: (value: string, caretStart: number, caretEnd: number) => void
  onFormulaSessionChange?: (session: FormulaEditSession) => void
  onFinishFormulaEdit?: () => void
  showFunctionAssistant?: boolean
  onActivateFormulaSurface?: () => void
  onOpenInsertFunction?: () => void
}

export default function FormulaBar({
  controller,
  liveValue,
  formulaSession,
  currentSheetName,
  expanded = false,
  definedNames = [],
  onBeginFormulaEdit,
  onDraftChange,
  onFormulaSessionChange,
  onFinishFormulaEdit,
  showFunctionAssistant = true,
  onActivateFormulaSurface,
  onOpenInsertFunction,
}: FormulaBarProps) {
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const [value, setValue] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [activeNameSuggestionIndex, setActiveNameSuggestionIndex] = useState(0)

  const skipNextBlurRef = useRef(false)

  // Sync with live value from Cell Editor
  useEffect(() => {
    if (formulaSession) {
      setValue(formulaSession.draft)
      requestAnimationFrame(() => {
        const node = inputRef.current
        if (node && document.activeElement === node) {
          node.setSelectionRange(formulaSession.caretStart, formulaSession.caretEnd)
        }
      })
      return
    }
    if (liveValue !== undefined && liveValue !== null) {
      setValue(liveValue)
    }
  }, [formulaSession, liveValue])

  // Sync with selection
  useEffect(() => {
    // If we are receiving a live value, don't overwrite with selection data
    if (isEditing || (liveValue !== undefined && liveValue !== null)) return

    const selection = controller.selection
    if (!selection) {
      setValue('')
      return
    }

    const { col, row } = selection
    const formula = controller.getCellFormula(col, row)
    if (formula !== undefined) {
      setValue(formula)
    } else {
      const val = controller.getCellValue(col, row)
      setValue(val || '')
    }
  }, [controller, controller.selection, isEditing, liveValue])

  // Actually, parent passes down controller. Parent re-renders on selection change.
  // So this effect will run on every parent render.
  // We need to ensure we don't overwrite if editing.

  const functionAssistance = useMemo(() => (
    formulaSession
      ? getFormulaFunctionAssistance(value, formulaSession.caretEnd, formulaSession.functionSuggestionIndex)
      : null
  ), [formulaSession, value])
  const nameAutocomplete = useMemo(() => {
    if (!formulaSession || functionAssistance?.suggestions.length) return null
    return getFormulaNameAutocomplete(value, formulaSession.caretEnd, definedNames)
  }, [definedNames, formulaSession, functionAssistance?.suggestions.length, value])
  const nameSuggestionIndex = nameAutocomplete
    ? Math.min(activeNameSuggestionIndex, nameAutocomplete.suggestions.length - 1)
    : 0

  useEffect(() => {
    setActiveNameSuggestionIndex(0)
  }, [nameAutocomplete?.query, nameAutocomplete?.startOffset])

  const handleFocus = () => {
    setIsEditing(true)
    onActivateFormulaSurface?.()
    onBeginFormulaEdit?.(value, 0, value.length)
    setTimeout(() => {
      const node = inputRef.current
      if (node) {
        if (isActiveFormulaReferenceEditSession(formulaSession)) {
          node.setSelectionRange(formulaSession.caretStart, formulaSession.caretEnd)
        } else {
          node.setSelectionRange(0, node.value.length)
        }
      }
    }, 0)
  }

  const commit = (): boolean => {
    if (!canCommitFormulaSessionOnSheet(formulaSession, formulaSession?.activeSheetName ?? currentSheetName)) return false
    const selection = controller.selection
    if (selection) {
      const { col, row } = selection
      // Determine if it's a formula or value?
      // streamer.setCellValue handles implementation details.
      controller.setCellValue(col, row, formulaSession?.draft ?? value)
    }
    return true
  }

  const moveSelectionAfterCommit = (navigation: FormulaCommitNavigation | null | undefined) => {
    if (!navigation || navigation === 'none') return
    const sel = controller.selection
    if (!sel) return
    const colCount = Math.max(1, controller.getColCount())
    const rowCount = Math.max(1, controller.getRowCount())
    const nextCol = Math.min(
      Math.max(0, navigation === 'right' ? sel.col + 1 : navigation === 'left' ? sel.col - 1 : sel.col),
      colCount - 1,
    )
    const nextRow = Math.min(
      Math.max(0, navigation === 'down' ? sel.row + 1 : navigation === 'up' ? sel.row - 1 : sel.row),
      rowCount - 1,
    )
    controller.selection = { ...sel, row: nextRow, endRow: nextRow, col: nextCol, endCol: nextCol }
    controller.notifySelectionChange()
    controller.scrollToCell(nextCol, nextRow)
    controller.requestRender()
  }

  const handleBlur = () => {
    if (skipNextBlurRef.current) {
      skipNextBlurRef.current = false;
      return;
    }
    if (isActiveFormulaReferenceEditSession(formulaSession)) return;
    setIsEditing(false)
    commit()
    onFinishFormulaEdit?.()
  }

  const handleNameSuggestionSelect = (name: string) => {
    if (!formulaSession || !nameAutocomplete) return
    const replaced = `${formulaSession.draft.slice(0, nameAutocomplete.startOffset)}${name}${formulaSession.draft.slice(formulaSession.caretEnd)}`
    const caret = nameAutocomplete.startOffset + name.length
    const nextSession = updateFormulaDraft(formulaSession, replaced, caret, caret)
    onFormulaSessionChange?.(nextSession)
    setValue(nextSession.draft)
    requestAnimationFrame(() => {
      inputRef.current?.setSelectionRange(nextSession.caretStart, nextSession.caretEnd)
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (formulaSession) {
      if (nameAutocomplete?.suggestions.length && !functionAssistance?.suggestions.length) {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault()
          e.stopPropagation()
          const delta = e.key === 'ArrowDown' ? 1 : -1
          const count = nameAutocomplete.suggestions.length
          setActiveNameSuggestionIndex((current) => (current + delta + count) % count)
          return
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault()
          e.stopPropagation()
          handleNameSuggestionSelect(nameAutocomplete.suggestions[nameSuggestionIndex]?.name ?? nameAutocomplete.suggestions[0].name)
          return
        }
      }
      const command = {
        key: e.key,
        shiftKey: e.shiftKey,
        ctrlKey: e.ctrlKey,
        metaKey: e.metaKey,
        altKey: e.altKey,
      }
      const keyDecision = resolveFormulaKeyCommand(formulaSession, command)
      const nextSession = applyFormulaKeyCommand(formulaSession, command)
      if (nextSession !== formulaSession) {
        e.preventDefault()
        if (keyDecision.stopPropagation) e.stopPropagation()
        if (nextSession.status === 'committed') {
          if (!commit()) return
          onFormulaSessionChange?.(nextSession)
          moveSelectionAfterCommit(nextSession.commitNavigation)
          setIsEditing(false)
          skipNextBlurRef.current = true
          onFinishFormulaEdit?.()
          inputRef.current?.blur()
          return
        }
        if (nextSession.status === 'cancelled') {
          onFormulaSessionChange?.(nextSession)
          setIsEditing(false)
          onFinishFormulaEdit?.()
          inputRef.current?.blur()
          return
        }
        onFormulaSessionChange?.(nextSession)
        if (nextSession.draft !== value) {
          setValue(nextSession.draft)
        }
        return
      }
      if (keyDecision.preventDefault) e.preventDefault()
      if (keyDecision.stopPropagation) e.stopPropagation()
      if (keyDecision.disposition === 'host-suppressed') return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      if (!commit()) return
      moveSelectionAfterCommit(e.shiftKey ? 'up' : 'down')
      setIsEditing(false)
      // inputRef.current?.blur() // Optional: keep focus?
      // Usually formula bar keeps focus if explicit? Excel behavior: Enter moves selection and focuses new cell (blurs bar).
      skipNextBlurRef.current = true;
      onFinishFormulaEdit?.()
      inputRef.current?.blur()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setIsEditing(false)
      // Revert
      const selection = controller.selection
      if (selection) {
        const { col, row } = selection
        const formula = controller.getCellFormula(col, row)
        setValue(formula !== undefined ? formula : (controller.getCellValue(col, row) || ''))
      }
      onFinishFormulaEdit?.()
      inputRef.current?.blur()
    } else if (e.key === 'Tab') {
      e.preventDefault()
      if (!commit()) return
      moveSelectionAfterCommit(e.shiftKey ? 'left' : 'right')
      setIsEditing(false)
      skipNextBlurRef.current = true;
      onFinishFormulaEdit?.()
      inputRef.current?.blur()
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value
    if (!isEditing) {
      setIsEditing(true)
      onActivateFormulaSurface?.()
    }
    setValue(newValue)
    onDraftChange?.(
      newValue,
      e.target.selectionStart ?? newValue.length,
      e.target.selectionEnd ?? e.target.selectionStart ?? newValue.length,
    )
  }

  const handleFormulaSuggestionSelect = (functionName: string) => {
    if (!formulaSession) return
    const nextSession = insertFormulaFunctionSuggestion(formulaSession, functionName)
    if (nextSession === formulaSession) return
    onFormulaSessionChange?.(nextSession)
    if (nextSession.draft !== value) {
      setValue(nextSession.draft)
    }
    requestAnimationFrame(() => {
      inputRef.current?.setSelectionRange(nextSession.caretStart, nextSession.caretEnd)
    })
  }

  const handleSelectionChange = (target: HTMLTextAreaElement) => {
    if (!formulaSession) return
    const caretStart = target.selectionStart ?? target.value.length
    const caretEnd = target.selectionEnd ?? caretStart
    const nextSession = updateFormulaSelection(formulaSession, caretStart, caretEnd)
    if (
      nextSession.caretStart === formulaSession.caretStart &&
      nextSession.caretEnd === formulaSession.caretEnd &&
      nextSession.activeReferenceId === formulaSession.activeReferenceId &&
      nextSession.mode === formulaSession.mode &&
      nextSession.functionSuggestionIndex === formulaSession.functionSuggestionIndex
    ) {
      return
    }
    onFormulaSessionChange?.(nextSession)
  }

  const formulaTextActive = Boolean(formulaSession && formulaSession.mode !== 'value')
  const formulaTextStyle: React.CSSProperties = {
    padding: '2px 7px',
    border: '1px solid rgba(209,213,219,0.8)',
    borderRadius: 4,
    fontSize: '13px',
    fontFamily: 'monospace',
    minHeight: '22px',
    lineHeight: '22px',
    boxSizing: 'border-box',
  }

  return (
    <div
      className="formula-bar fg-sheet-formula-surface"
      style={{
        display: 'flex',
        alignItems: 'stretch',
        padding: '2px 4px',
        borderBottom: '1px solid rgba(209, 213, 219, 0.88)',
        backgroundColor: 'rgba(255, 255, 255, 0.82)',
        borderRadius: 4,
        width: '100%',
      }}
    >
      <button
        type="button"
        className="fg-excel-formula-function-button"
        style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '28px',
        color: '#5f6368',
        fontStyle: 'italic',
        fontWeight: 'bold',
        fontSize: '14px',
        userSelect: 'none',
        background: 'rgba(255,255,255,0.5)',
        border: '1px solid rgba(209,213,219,0.75)',
        borderRadius: 4,
        padding: '0 6px',
      }}
        title="Insert Function (Shift+F3)"
        aria-label="Insert Function"
        onMouseDown={(event) => event.preventDefault()}
        onClick={onOpenInsertFunction}
      >
        fx
      </button>
      <div className="fg-excel-formula-divider" style={{ width: '1px', backgroundColor: '#e0e0e0', margin: '0 6px' }} />
      <div
        style={{
          flex: 1,
          width: '100%',
          position: 'relative',
          minHeight: '28px',
        }}
      >
        <FormulaSyntaxOverlay
          session={formulaSession}
          text={value}
          style={{
            ...formulaTextStyle,
            backgroundColor: 'rgba(255,255,255,0.75)',
            maxHeight: '240px',
          }}
        />
        <FormulaFunctionAssistant
          session={showFunctionAssistant ? formulaSession : null}
          text={value}
          nameSuggestions={nameAutocomplete?.suggestions ?? []}
          activeNameSuggestionIndex={nameSuggestionIndex}
          onSelectSuggestion={handleFormulaSuggestionSelect}
          onSelectNameSuggestion={handleNameSuggestionSelect}
          style={{
            top: 'calc(100% + 6px)',
          }}
        />
        <textarea
          ref={inputRef}
          id="formula-bar-input"
          name="formula-bar-input"
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onSelect={(event) => handleSelectionChange(event.currentTarget)}
          onClick={(event) => handleSelectionChange(event.currentTarget)}
          onKeyUp={(event) => handleSelectionChange(event.currentTarget)}
          placeholder=""
          rows={expanded ? 3 : 1}
          aria-label="Formula bar"
          title="Formula bar"
          spellCheck={false}
          autoComplete="off"
          autoCorrect="off"
          data-form-type="other"
          data-lpignore="true"
          data-1p-ignore="true"
          data-formula-local-editing={isEditing ? '1' : '0'}
          data-formula-has-draft-handler={onDraftChange ? '1' : '0'}
          data-formula-session-mode={formulaSession?.mode ?? ''}
          data-formula-session-status={formulaSession?.status ?? ''}
          data-formula-anchor-sheet={formulaSession?.anchorSheetName ?? ''}
          data-formula-active-sheet={formulaSession?.activeSheetName ?? ''}
          style={{
            ...formulaTextStyle,
            position: 'relative',
            zIndex: 1,
            display: 'block',
            outline: 'none',
            backgroundColor: formulaTextActive ? 'transparent' : 'rgba(255,255,255,0.75)',
            color: formulaTextActive ? 'transparent' : '#111827',
            caretColor: '#111827',
            resize: 'none',
            maxHeight: '240px',
            width: '100%',
            overflow: expanded ? 'auto' : 'hidden',
          }}
        />
      </div>
    </div>
  )
}
